import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, GoldButton, GhostButton } from "@/components/admin/AdminLayout";
import AdminSeasonMatrix from "@/components/admin/AdminSeasonMatrix";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, X, Trash2, MapPin, Upload, Image as ImageIcon, Pencil, Star } from "lucide-react";
import { useLang } from "@/lib/lang";

const ZONES = ["Lisboa", "Sintra", "Cascais", "Setubal", "Alentejo", "Algarve", "Porto", "Madeira", "Acores"];
const MAX_PHOTOS = 6;
const MAX_PHOTO_BYTES = 4 * 1024 * 1024; // 4MB per file

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/uploads", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.url; // "/api/uploads/<id>"
}

export default function Tours() {
  const { t: tr } = useLang();
  const [tours, setTours] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reviewsFor, setReviewsFor] = useState(null);
  const load = () => api.get("/admin/tours").then((r) => setTours(r.data));
  useEffect(() => { load(); }, []);
  const toggleActive = async (t) => { await api.patch(`/admin/tours/${t.id}`, { active: !t.active }); load(); };
  const remove = async (t) => {
    if (!window.confirm(`${tr("action_delete")} ${t.slug}?`)) return;
    await api.delete(`/admin/tours/${t.id}`); load();
  };
  return (
    <div className="p-10 max-w-[1600px]">
      <PageHeader eyebrow="—" title={tr("nav_tours")} description="—">
        <GoldButton onClick={() => setShow(true)} data-testid="new-tour-btn"><Plus className="w-4 h-4" /> {tr("tour_new_title")}</GoldButton>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {tours.length === 0 && <div className="col-span-3 border border-dashed border-slate-200 p-16 text-center text-slate-500">—</div>}
        {tours.map((t) => {
          const pt = (t.translations || []).find(x => x.lang === "PT") || (t.translations || [])[0] || {};
          const cover = t.cover_image || (t.images || [])[0];
          const stopsCount = (t.stops || []).length;
          const photosCount = (t.images || []).length;
          return (
            <div key={t.id} data-testid={`tour-${t.id}`} className="border border-slate-200 bg-white overflow-hidden">
              {cover && <div className="h-40 bg-cover bg-center" style={{ backgroundImage: `url(${cover})` }} />}
              <div className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <span className="label-caps text-[#f59e0b]">{t.zone}</span>
                  <button onClick={() => toggleActive(t)} data-testid={`tour-toggle-${t.id}`} className={`text-[10px] uppercase px-2 py-0.5 border font-bold ${t.active ? "border-emerald-500 text-emerald-700 bg-emerald-50" : "border-slate-400 text-slate-600"}`}>
                    {t.active ? tr("tours_active_badge") : tr("tours_draft_badge")}
                  </button>
                </div>
                <h3 className="font-display text-lg text-[#0a0f1e]">{pt.title || t.slug}</h3>
                <p className="text-xs text-slate-600 mt-1 line-clamp-2">{pt.short_description}</p>
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-xs text-slate-700">{t.duration_hours}h · {t.pricing_model.replace("_", " ")}</div>
                  <div className="font-display text-xl text-[#f59e0b] font-bold">€{t.price_base.toFixed(0)}</div>
                </div>
                <div className="mt-3 flex items-center gap-3 text-[11px] text-slate-700 font-semibold">
                  <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {stopsCount} paragens</span>
                  <span className="inline-flex items-center gap-1"><ImageIcon className="w-3 h-3" /> {photosCount}/{MAX_PHOTOS} fotos</span>
                </div>
                <div className="mt-3 flex gap-2 pt-3 border-t border-slate-100">
                  <button onClick={() => setEditing(t)} data-testid={`tour-edit-${t.id}`} className="text-xs text-[#0a0f1e] hover:text-[#f59e0b] flex items-center gap-1 font-semibold uppercase tracking-wide"><Pencil className="w-3 h-3" /> {tr("action_edit")}</button>
                  <button onClick={() => setReviewsFor(t)} data-testid={`tour-reviews-${t.id}`} className="text-xs text-[#0a0f1e] hover:text-[#f59e0b] flex items-center gap-1 font-semibold uppercase tracking-wide"><Star className="w-3 h-3" /> Avaliações</button>
                  <button onClick={() => remove(t)} className="text-xs text-slate-600 hover:text-red-500 flex items-center gap-1 font-semibold ml-auto"><Trash2 className="w-3 h-3" /> {tr("action_delete")}</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {show && <TourEditor onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
      {editing && <TourEditor tour={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {reviewsFor && <TourReviewsModal tour={reviewsFor} onClose={() => setReviewsFor(null)} />}
    </div>
  );
}

function TourEditor({ tour, onClose, onSaved }) {
  const { t: tr } = useLang();
  const isEdit = !!tour;
  const emptyPT = { lang: "PT", title: "", short_description: "", full_description: "", included: [], not_included: [], meeting_point: "", itinerary_stops: [] };
  const initial = () => {
    if (isEdit) {
      const translations = tour.translations && tour.translations.length ? tour.translations : [emptyPT];
      const hasPT = translations.some(x => x.lang === "PT");
      return {
        ...tour,
        images: tour.images || [],
        cover_image: tour.cover_image || (tour.images || [])[0] || "",
        stops: (tour.stops || []).map((s, i) => ({ ...s, id: s.id || crypto.randomUUID(), order: s.order ?? i })),
        translations: hasPT ? translations : [emptyPT, ...translations],
        vehicle_pricing: tour.vehicle_pricing || [],
        season_prices: tour.season_prices || {},
        min_advance_hours: tour.min_advance_hours ?? 0,
      };
    }
    return {
      slug: "", zone: "Lisboa", duration_hours: 3, price_base: 150, pricing_model: "per_vehicle",
      cover_image: "", images: [],
      stops: [{ id: crypto.randomUUID(), order: 0, name: "", address: "", duration_min: 30, notes: "" }],
      translations: [emptyPT],
      vehicle_pricing: [],
      season_prices: {},
      min_advance_hours: 0,
    };
  };
  const [f, setF] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [zones, setZones] = useState([]);
  const fileInput = useRef(null);
  const ptIndex = f.translations.findIndex(x => x.lang === "PT");
  const updT = (key, val) => setF({ ...f, translations: f.translations.map((tr, idx) => idx === ptIndex ? { ...tr, [key]: val } : tr) });
  const ptEntry = f.translations[ptIndex] || emptyPT;

  useEffect(() => {
    api.get("/admin/vehicle-categories?active=true").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/admin/seasons?active=true").then((r) => setSeasons(r.data)).catch(() => {});
    api.get("/admin/zones?active=true").then((r) => setZones(r.data)).catch(() => {});
  }, []);

  const priceOf = (key) => {
    const row = (f.vehicle_pricing || []).find((r) => r.category_key === key);
    return row?.price_eur ?? "";
  };
  const setPriceOf = (key, value) => {
    const num = value === "" ? 0 : Number(value);
    setF((prev) => {
      const rest = (prev.vehicle_pricing || []).filter((r) => r.category_key !== key);
      if (num > 0) rest.push({ category_key: key, price_eur: num });
      return { ...prev, vehicle_pricing: rest };
    });
  };

  const addStop = () => setF({ ...f, stops: [...f.stops, { id: crypto.randomUUID(), order: f.stops.length, name: "", address: "", duration_min: 30, notes: "" }] });
  const removeStop = (i) => setF({ ...f, stops: f.stops.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, order: idx })) });
  const updStop = (i, key, val) => setF({ ...f, stops: f.stops.map((s, idx) => idx === i ? { ...s, [key]: val } : s) });
  const moveStop = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= f.stops.length) return;
    const arr = [...f.stops];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setF({ ...f, stops: arr.map((s, idx) => ({ ...s, order: idx })) });
  };

  const addPhotos = async (files) => {
    const remaining = MAX_PHOTOS - f.images.length;
    if (remaining <= 0) { toast.error(`Máximo ${MAX_PHOTOS} fotos por tour`); return; }
    const arr = Array.from(files).slice(0, remaining);
    for (const file of arr) {
      if (file.size > MAX_PHOTO_BYTES) { toast.error(`${file.name} excede 4MB`); continue; }
      try {
        const url = await uploadImage(file);
        setF((prev) => ({ ...prev, images: [...prev.images, url], cover_image: prev.cover_image || url }));
      } catch (err) {
        toast.error(`Erro ao enviar ${file.name}: ${formatApiError(err)}`);
      }
    }
  };
  const removePhoto = (i) => setF((prev) => {
    const removed = prev.images[i];
    const imgs = prev.images.filter((_, idx) => idx !== i);
    return { ...prev, images: imgs, cover_image: prev.cover_image === removed ? (imgs[0] || "") : prev.cover_image };
  });
  const setCover = (url) => setF({ ...f, cover_image: url });

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const payload = {
        slug: f.slug,
        zone: f.zone,
        duration_hours: f.duration_hours,
        price_base: f.price_base,
        pricing_model: f.pricing_model,
        cover_image: f.cover_image,
        images: f.images,
        stops: f.stops.filter(s => s.name || s.address),
        translations: f.translations,
        vehicle_pricing: (f.vehicle_pricing || []).filter((r) => r.category_key && Number(r.price_eur) > 0),
        season_prices: f.season_prices || {},
        min_advance_hours: Number(f.min_advance_hours) || 0,
      };
      if (isEdit) {
        await api.patch(`/admin/tours/${tour.id}`, payload);
        toast.success(tr("toast_tour_updated"));
      } else {
        await api.post("/admin/tours", payload);
        toast.success(tr("toast_tour_created"));
      }
      onSaved();
    }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const retranslate = async () => {
    if (!ptEntry.title) { toast.error(tr("tours_fill_pt_first")); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/admin/tours/translate", { pt: ptEntry });
      const derived = data.translations || [];
      const merged = [ptEntry, ...derived];
      setF({ ...f, translations: merged });
      toast.success(`Traduzido para ${derived.length} idiomas`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const nonPtLangs = (f.translations || []).filter(x => x.lang !== "PT").map(x => x.lang);

  return (
    <div className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-6">
      <form onSubmit={submit} className="bg-white border border-[#f59e0b]/30 w-full max-w-4xl p-8 max-h-[92vh] overflow-y-auto" data-testid={isEdit ? "edit-tour-modal" : "create-tour-modal"}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-display text-2xl text-[#0a0f1e]">{isEdit ? tr("tour_edit_title") : tr("tour_new_title")}</h3>
            {isEdit && <p className="text-xs text-slate-500 mt-1">{tour.slug}</p>}
          </div>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <F l="Slug"><Input required value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} className="input-dt" placeholder="sintra-full-day" data-testid="tour-slug" /></F>
          <F l="Zona">
            <select value={f.zone} onChange={(e) => setF({ ...f, zone: e.target.value })} className="input-dt" data-testid="tour-zone">
              {ZONES.map((z) => <option key={z}>{z}</option>)}
            </select>
          </F>
          <F l="Duração (h)"><Input type="number" step="0.5" value={f.duration_hours} onChange={(e) => setF({ ...f, duration_hours: Number(e.target.value) })} className="input-dt" /></F>
          <F l="Preço base (€)"><Input type="number" value={f.price_base} onChange={(e) => setF({ ...f, price_base: Number(e.target.value) })} className="input-dt" data-testid="tour-price" /></F>
          <F l="Antecedência mín. (horas)"><Input type="number" min="0" step="0.5" value={f.min_advance_hours} onChange={(e) => setF({ ...f, min_advance_hours: Number(e.target.value) })} className="input-dt" data-testid="tour-min-advance" placeholder="0 = sem restrição" /></F>
          <F l="Modelo de preço">
            <select value={f.pricing_model} onChange={(e) => setF({ ...f, pricing_model: e.target.value })} className="input-dt">
              <option value="per_vehicle">Por veículo</option>
              <option value="per_person">Por pessoa</option>
            </select>
          </F>
        </div>

        {/* VEHICLE PRICING GRID */}
        <div className="border-t border-slate-200 pt-5 mt-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="label-caps">Preço por categoria de veículo</p>
              <p className="text-[11px] text-slate-500 mt-0.5">Deixe em branco para desactivar essa categoria neste tour. O cliente só verá cards das categorias com preço definido.</p>
            </div>
            <a href="/admin/vehicle-categories" target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-widest text-[#f59e0b] hover:underline font-bold">Gerir categorias →</a>
          </div>
          {categories.length === 0 ? (
            <div className="text-xs text-slate-500 border border-dashed border-slate-300 p-4 text-center">
              Sem categorias configuradas. Crie categorias em <a href="/admin/vehicle-categories" className="text-[#f59e0b] underline">/admin/vehicle-categories</a>.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {categories.map((c) => {
                const p = priceOf(c.key);
                const enabled = p !== "" && Number(p) > 0;
                return (
                  <div key={c.key} className={`border p-3 flex items-center gap-3 ${enabled ? "border-[#f59e0b] bg-[#f59e0b]/5" : "border-slate-200 bg-white"}`} data-testid={`tour-vp-${c.key}`}>
                    {c.image_url ? (
                      <img src={c.image_url} alt={c.label} className="w-14 h-14 object-cover border border-slate-200" />
                    ) : (
                      <div className="w-14 h-14 bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400 text-[10px] uppercase">{c.key.slice(0, 3)}</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#0a0f1e] truncate">{c.label}</div>
                      <div className="text-[10px] text-slate-500">{c.capacity_pax} pax · {c.capacity_bags} malas</div>
                      <div className="mt-1.5 flex items-center gap-1.5">
                        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">€</span>
                        <Input
                          type="number" min="0" step="1"
                          value={p}
                          onChange={(e) => setPriceOf(c.key, e.target.value)}
                          placeholder="—"
                          className="input-dt h-8 text-sm w-20"
                          data-testid={`tour-vp-price-${c.key}`}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* SEASON PRICING MATRIX (Msg 658) */}
        <div className="border-t border-slate-200 pt-5 mt-6">
          <p className="label-caps mb-3">Preços por temporada (opcional)</p>
          <p className="text-[11px] text-slate-500 mt-0.5 mb-3">
            Valores específicos por temporada e categoria. Se não preencher, é usado o preço base acima. As temporadas aplicáveis dependem da zona seleccionada.
          </p>
          <AdminSeasonMatrix
            seasons={seasons}
            categories={categories}
            zoneKey={(zones.find((z) => z.label === f.zone)?.key) || (f.zone || "").toLowerCase()}
            seasonPrices={f.season_prices || {}}
            onChange={(sp) => setF({ ...f, season_prices: sp })}
            basePrices={Object.fromEntries((f.vehicle_pricing || []).map((r) => [r.category_key, r.price_eur]))}
            testIdPrefix="adm-tour-season"
          />
        </div>

        {/* PHOTOS */}
        <div className="border-t border-slate-200 pt-5 mt-6">
          <div className="flex items-center justify-between mb-3">
            <p className="label-caps">Fotos ({f.images.length}/{MAX_PHOTOS})</p>
            <button type="button" onClick={() => fileInput.current?.click()} disabled={f.images.length >= MAX_PHOTOS} className="text-[11px] uppercase tracking-widest px-3 py-1.5 border border-slate-300 hover:border-[#f59e0b] disabled:opacity-40 flex items-center gap-1 font-bold" data-testid="tour-add-photos">
              <Upload className="w-3 h-3" /> Adicionar fotos
            </button>
            <input ref={fileInput} type="file" accept="image/*" multiple hidden onChange={(e) => addPhotos(e.target.files)} data-testid="tour-photo-input" />
          </div>
          {f.images.length === 0 ? (
            <div className="border border-dashed border-slate-300 p-8 text-center text-xs text-slate-500">Sem fotos. Máximo {MAX_PHOTOS} imagens (JPG/PNG/WebP, até 4MB cada). A primeira será a foto de capa.</div>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {f.images.map((img, i) => (
                <div key={i} className="relative aspect-square border border-slate-200 group" data-testid={`tour-photo-${i}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                  {f.cover_image === img && <span className="absolute top-1 left-1 text-[9px] uppercase bg-[#f59e0b] text-[#0a0f1e] px-1.5 py-0.5 font-bold">Capa</span>}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                    {f.cover_image !== img && (
                      <button type="button" onClick={() => setCover(img)} className="text-[10px] px-2 py-1 bg-white text-[#0a0f1e] font-bold uppercase">Capa</button>
                    )}
                    <button type="button" onClick={() => removePhoto(i)} className="text-[10px] px-2 py-1 bg-red-600 text-white font-bold uppercase" data-testid={`tour-photo-remove-${i}`}>Rem</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* STOPS */}
        <div className="border-t border-slate-200 pt-5 mt-6">
          <div className="flex items-center justify-between mb-3">
            <p className="label-caps">Paragens ({f.stops.length})</p>
            <button type="button" onClick={addStop} className="text-[11px] uppercase tracking-widest px-3 py-1.5 border border-slate-300 hover:border-[#f59e0b] flex items-center gap-1 font-bold" data-testid="tour-add-stop">
              <Plus className="w-3 h-3" /> Adicionar paragem
            </button>
          </div>
          <div className="space-y-2">
            {f.stops.map((s, i) => (
              <div key={s.id} className="border border-slate-200 p-3 bg-slate-50" data-testid={`tour-stop-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex flex-col gap-0.5">
                    <button type="button" onClick={() => moveStop(i, -1)} disabled={i === 0} className="text-[10px] text-slate-500 hover:text-[#f59e0b] disabled:opacity-30">▲</button>
                    <button type="button" onClick={() => moveStop(i, 1)} disabled={i === f.stops.length - 1} className="text-[10px] text-slate-500 hover:text-[#f59e0b] disabled:opacity-30">▼</button>
                  </div>
                  <div className="w-6 h-6 bg-[#f59e0b] text-[#0a0f1e] flex items-center justify-center text-xs font-bold">{i + 1}</div>
                  <Input placeholder="Nome (ex: Palácio da Pena)" value={s.name} onChange={(e) => updStop(i, "name", e.target.value)} className="input-dt flex-1" data-testid={`stop-name-${i}`} />
                  <button type="button" onClick={() => removeStop(i)} className="text-slate-500 hover:text-red-600" data-testid={`stop-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-3 gap-2 pl-10">
                  <div className="col-span-2">
                    <Input placeholder="Morada" value={s.address} onChange={(e) => updStop(i, "address", e.target.value)} className="input-dt" />
                  </div>
                  <div>
                    <Input type="number" min={5} step={5} placeholder="Min" value={s.duration_min} onChange={(e) => updStop(i, "duration_min", parseInt(e.target.value || "0"))} className="input-dt" />
                  </div>
                  <div className="col-span-3">
                    <Input placeholder="Notas / recomendações" value={s.notes} onChange={(e) => updStop(i, "notes", e.target.value)} className="input-dt" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CONTENT */}
        <div className="border-t border-slate-200 pt-5 mt-6">
          <div className="flex items-center justify-between mb-3">
            <p className="label-caps">Conteúdo (PT — idioma-fonte)</p>
            <button type="button" onClick={retranslate} disabled={loading} className="text-[11px] uppercase tracking-widest px-3 py-1.5 border border-slate-300 hover:border-[#f59e0b] disabled:opacity-50 font-bold" data-testid="tour-retranslate">
              Retraduzir EN/ES/FR/DE
            </button>
          </div>
          <div className="space-y-3">
            <Input placeholder={tr("tours_title_field")} value={ptEntry.title} onChange={(e) => updT("title", e.target.value)} className="input-dt" data-testid="tour-title" />
            <Textarea placeholder="Descrição curta" value={ptEntry.short_description} onChange={(e) => updT("short_description", e.target.value)} className="input-dt" rows={2} />
            <Textarea placeholder="Descrição completa" value={ptEntry.full_description} onChange={(e) => updT("full_description", e.target.value)} className="input-dt" rows={4} />
            <Input placeholder="Ponto de encontro" value={ptEntry.meeting_point} onChange={(e) => updT("meeting_point", e.target.value)} className="input-dt" />

            {/* Msg 727: itinerário textual + incluído/não-incluído */}
            <ListField
              label="Itinerário (pontos textuais)"
              placeholder="Ex: Passagem por Belém"
              items={ptEntry.itinerary_stops || []}
              onChange={(items) => updT("itinerary_stops", items)}
              testIdPrefix="itin"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ListField
                label="Incluído"
                placeholder="Ex: Água engarrafada"
                items={ptEntry.included || []}
                onChange={(items) => updT("included", items)}
                testIdPrefix="incl"
              />
              <ListField
                label="Não incluído"
                placeholder="Ex: Entradas em monumentos"
                items={ptEntry.not_included || []}
                onChange={(items) => updT("not_included", items)}
                testIdPrefix="notincl"
              />
            </div>

            {nonPtLangs.length > 0 ? (
              <p className="text-[11px] text-emerald-700 font-semibold">Traduções ativas: {nonPtLangs.join(" · ")}</p>
            ) : (
              <p className="text-[11px] text-slate-500">Após guardar, as traduções EN/ES/FR/DE são geradas automaticamente a partir do PT via Gemini.</p>
            )}
          </div>
        </div>

        <div className="flex gap-3 mt-8 pt-4 border-t border-slate-200">
          <GhostButton type="button" onClick={onClose} className="flex-1">Cancelar</GhostButton>
          <GoldButton type="submit" disabled={loading} className="flex-1 justify-center" data-testid="tour-submit">{loading ? tr("action_saving") : (isEdit ? tr("toast_save_changes") : tr("action_create"))}</GoldButton>
        </div>
      </form>
    </div>
  );
}
function F({ l, children }) { return (<div><label className="label-caps block mb-2">{l}</label>{children}</div>); }

function ListField({ label, placeholder, items, onChange, testIdPrefix }) {
  const [draft, setDraft] = React.useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...(items || []), v]);
    setDraft("");
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, val) => onChange(items.map((it, idx) => idx === i ? val : it));
  return (
    <div>
      <label className="label-caps block mb-1.5">{label}</label>
      <div className="space-y-1.5">
        {(items || []).map((it, i) => (
          <div key={i} className="flex gap-2 items-center" data-testid={`${testIdPrefix}-item-${i}`}>
            <span className="text-[10px] text-slate-400 font-mono w-5 shrink-0">#{i + 1}</span>
            <Input value={it} onChange={(e) => update(i, e.target.value)} className="input-dt !h-9 flex-1" />
            <button type="button" onClick={() => remove(i)} className="text-red-500 hover:text-red-700 shrink-0" data-testid={`${testIdPrefix}-rm-${i}`}>×</button>
          </div>
        ))}
        <div className="flex gap-2 items-center">
          <Input placeholder={placeholder} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} className="input-dt !h-9 flex-1" data-testid={`${testIdPrefix}-draft`} />
          <button type="button" onClick={add} className="h-9 px-3 border border-slate-300 hover:border-[#f59e0b] text-xs uppercase tracking-wide font-bold" data-testid={`${testIdPrefix}-add`}>+ Adicionar</button>
        </div>
      </div>
    </div>
  );
}



function TourReviewsModal({ tour, onClose }) {
  const [reviews, setReviews] = React.useState(null);
  const load = () => api.get(`/admin/reviews?tour_id=${tour.id}`).then((r) => setReviews(r.data)).catch((e) => toast.error(formatApiError(e)));
  React.useEffect(() => { load(); }, [tour.id]);

  const del = async (r) => {
    if (!window.confirm(`Apagar avaliação de ${r.client_name || r.author || "cliente"}? Esta acção não pode ser desfeita.`)) return;
    try {
      await api.delete(`/admin/reviews/${r.id}`);
      toast.success("Avaliação eliminada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const pt = (tour.translations || []).find(x => x.lang === "PT") || (tour.translations || [])[0] || {};
  const avg = reviews && reviews.length ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length) : null;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" data-testid="tour-reviews-modal">
      <div className="bg-white border border-[#f59e0b]/30 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-bold">Avaliações</p>
            <h3 className="font-display text-xl text-[#0a0f1e]">{pt.title || tour.slug}</h3>
            {avg !== null && (
              <div className="flex items-center gap-1 mt-1 text-sm">
                <Star className="w-4 h-4 text-[#f59e0b] fill-[#f59e0b]" />
                <strong>{avg.toFixed(1)}</strong>
                <span className="text-slate-500">({reviews.length} {reviews.length === 1 ? "avaliação" : "avaliações"})</span>
              </div>
            )}
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-6">
          {reviews === null && <div className="text-sm text-slate-500">A carregar…</div>}
          {reviews && reviews.length === 0 && (
            <div className="text-center py-10 text-sm text-slate-500 italic">Sem avaliações ainda para este tour.</div>
          )}
          {reviews && reviews.map((r) => (
            <div key={r.id} className="border-b border-slate-100 py-4 last:border-b-0" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-semibold text-[#0a0f1e]">{r.client_name || r.author || "Cliente anónimo"}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{r.created_at?.slice(0, 10) || ""}</div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-3.5 h-3.5 ${n <= (r.rating || 0) ? "text-[#f59e0b] fill-[#f59e0b]" : "text-slate-300"}`} />
                    ))}
                  </div>
                  <button onClick={() => del(r)} className="text-slate-400 hover:text-red-500" data-testid={`review-del-${r.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {r.comment && <p className="text-sm text-slate-700">{r.comment}</p>}
              {r.driver_id && <p className="text-[10px] text-slate-500 font-mono mt-1">Motorista: {r.driver_id.slice(0, 8)}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
